import React, { useEffect, useRef } from 'react';
import { ExternalLink, Instagram, Twitter } from 'lucide-react';

/**
 * Identify which social platform a URL belongs to.
 * Returns one of: 'instagram' | 'tiktok' | 'twitter_x' | null
 * We deliberately exclude YouTube here — it already has a dedicated `video_url` slot.
 */
export const detectSocialPostPlatform = (url) => {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'instagram.com' || host.endsWith('.instagram.com')) return 'instagram';
    if (host === 'tiktok.com' || host.endsWith('.tiktok.com')) return 'tiktok';
    if (host === 'twitter.com' || host === 'x.com') return 'twitter_x';
    return null;
  } catch {
    return null;
  }
};

/**
 * Extract the author handle (e.g. "@acme") from a social post URL.
 * Returns null if the handle can't be read from the URL shape.
 */
export const extractPostHandle = (url, platform) => {
  try {
    const u = new URL(url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`);
    const path = u.pathname;
    if (platform === 'tiktok') {
      const m = path.match(/\/@([\w.-]+)/);
      return m ? `@${m[1]}` : null;
    }
    if (platform === 'twitter_x') {
      const m = path.match(/^\/([\w]+)\/status\//);
      return m ? `@${m[1]}` : null;
    }
    // Instagram post URLs don't contain the author handle directly — the embed
    // script fills it in once loaded.
    return null;
  } catch {
    return null;
  }
};

export const SOCIAL_POST_LIMIT = 1;

// Cache loaded embed scripts so we don't inject the same <script> twice.
const loadedScripts = new Set();

const loadScript = (src) => {
  if (loadedScripts.has(src)) {
    if (src.includes('instagram')) window.instgrm?.Embeds?.process?.();
    if (src.includes('tiktok')) window.tiktokEmbed?.lib?.render?.(document.querySelectorAll('[data-tiktok-embed="pending"]'));
    if (src.includes('twitter')) window.twttr?.widgets?.load?.();
    return;
  }
  loadedScripts.add(src);
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  document.body.appendChild(s);
};

const InstagramEmbed = ({ url }) => (
  <blockquote
    className="instagram-media"
    data-instgrm-captioned
    data-instgrm-permalink={url}
    data-instgrm-version="14"
    style={{ background: '#fff', border: 0, margin: '0 auto', maxWidth: '540px', minWidth: '220px', padding: 0, width: '100%' }}
  >
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500">View this post on Instagram</a>
  </blockquote>
);

const TikTokEmbed = ({ url }) => {
  const match = url.match(/\/video\/(\d+)/);
  const videoId = match ? match[1] : '';
  return (
    <blockquote
      className="tiktok-embed"
      cite={url}
      data-video-id={videoId}
      style={{ maxWidth: '605px', minWidth: '240px', margin: '0 auto' }}
    >
      <section>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500">Watch on TikTok</a>
      </section>
    </blockquote>
  );
};

const TwitterEmbed = ({ url }) => (
  <blockquote className="twitter-tweet" data-dnt="true">
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500">View on X / Twitter</a>
  </blockquote>
);

const FallbackCard = ({ url }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2 p-4 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:border-slate-400"
  >
    <ExternalLink className="h-4 w-4" />
    <span className="truncate">{url}</span>
  </a>
);

/**
 * Renders a single featured social post.
 * Supports Instagram, TikTok, and X / Twitter via each platform's public embed script.
 * `onEngage` is fired the first time a user clicks anywhere on the embed (capture
 * phase) so we can report engagement to the business owner. Clicks inside the
 * final iframe are invisible to us, but the initial interaction (blockquote
 * border, caption link, fallback card) is always captured.
 */
const SocialPostEmbed = ({ url, onEngage }) => {
  const platform = detectSocialPostPlatform(url);
  const containerRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!platform) return;
    if (platform === 'instagram') loadScript('https://www.instagram.com/embed.js');
    if (platform === 'tiktok') loadScript('https://www.tiktok.com/embed.js');
    if (platform === 'twitter_x') loadScript('https://platform.twitter.com/widgets.js');
  }, [platform, url]);

  const handleClick = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onEngage?.();
  };

  if (!platform) return (
    <div onClick={handleClick}>
      <FallbackCard url={url} />
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="bg-white rounded-lg overflow-hidden border border-slate-200 p-3"
      data-testid={`social-post-embed-${platform}`}
      onClick={handleClick}
    >
      {platform === 'instagram' && <InstagramEmbed url={url} />}
      {platform === 'tiktok' && <TikTokEmbed url={url} />}
      {platform === 'twitter_x' && <TwitterEmbed url={url} />}
    </div>
  );
};

const PLATFORM_LABEL = { instagram: 'Instagram', tiktok: 'TikTok', twitter_x: 'X' };

const FeaturedCaption = ({ url, platform }) => {
  const handle = extractPostHandle(url, platform);
  const label = PLATFORM_LABEL[platform] || '';
  return (
    <p className="text-xs text-slate-500 mt-2 text-center">
      Pinned by the business
      {handle && <> · <span className="font-medium text-slate-700">{handle}</span></>}
      {label && <> · on {label}</>}
    </p>
  );
};

/**
 * Renders the Premium business's featured social post.
 * `variant="card"` renders compactly for the narrow sidebar (no outer padding,
 * no border — the parent `<Card>` already provides those).
 */
export const FeaturedSocialPosts = ({ urls, variant = 'section', onEngage }) => {
  if (!Array.isArray(urls)) return null;
  const valid = urls.filter(u => !!detectSocialPostPlatform(u)).slice(0, SOCIAL_POST_LIMIT);
  if (valid.length === 0) return null;

  const isCard = variant === 'card';
  const wrapperClass = isCard ? '' : 'mt-6 pt-6 border-t';
  const innerClass = isCard ? '' : 'max-w-xl mx-auto';

  return (
    <div className={wrapperClass} data-testid="listing-featured-posts">
      <h2 className={`${isCard ? 'text-base' : 'text-lg'} font-semibold text-slate-800 mb-3 flex items-center gap-2`}>
        <Instagram className={`${isCard ? 'h-4 w-4' : 'h-5 w-5'} text-slate-500`} aria-hidden="true" />
        Featured Post
      </h2>
      <div className={innerClass}>
        {valid.map((url, idx) => {
          const platform = detectSocialPostPlatform(url);
          return (
            <div key={idx}>
              <SocialPostEmbed url={url} onEngage={onEngage} />
              <FeaturedCaption url={url} platform={platform} />
            </div>
          );
        })}
      </div>
      {!isCard && (
        <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5 justify-center">
          <Twitter className="h-3 w-3" aria-hidden="true" />
          Loaded directly from Instagram, TikTok and X. No third-party tracking by Discover Essex.
        </p>
      )}
    </div>
  );
};

export default SocialPostEmbed;
